import { Controller, UseFormReturn } from 'react-hook-form';

import { IKeyValue } from 'shared/interfaces/utils/IKeyValue';
import { Button, Input, Select } from 'shared/theme/elements';
import {
  AdsBox,
  ChAside,
  ChBody,
  ChCard,
  ChFoot,
  ChGrid,
  ChHead,
  ChIc,
  ChSub,
  ChTitle,
  ChTitles,
  Dirty,
  Hint,
  Scope,
  Toggle,
  ToggleLabel,
  ToggleSub,
} from './ChannelSettings.styled';
import { IChannelRecording, SOURCE_FOLDER_ITEMS } from './channelRecording';

interface Props {
  form: UseFormReturn<IChannelRecording, object>;
  label: string;
  channelName?: string;
  machineItems: IKeyValue[];
  folderPlaceholder: string;
  hasPrograms: boolean;
  loading?: boolean;
  saving?: boolean;
  onRevert: () => void;
  onSave: () => void;
}

export const ChannelSettingsView = ({
  form,
  label,
  channelName,
  machineItems,
  folderPlaceholder,
  hasPrograms,
  loading,
  saving,
  onRevert,
  onSave,
}: Props) => {
  const {
    control,
    formState: { errors, isDirty },
  } = form;
  const disabled = loading || !hasPrograms;

  return (
    <ChCard>
      <ChHead>
        <ChIc>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="2" y="6" width="14" height="12" rx="2" />
            <path d="m22 8-6 4 6 4V8Z" />
          </svg>
        </ChIc>
        <ChTitles>
          <ChTitle>
            {label} Settings{channelName ? ` — ${channelName}` : ''}
          </ChTitle>
          <ChSub>
            Recording configuration and ads automation shared by every program on this {label.toLowerCase()}
          </ChSub>
        </ChTitles>
        <ChAside>
          {isDirty && <Dirty>Unsaved changes</Dirty>}
          <Scope>Applies to all programs</Scope>
        </ChAside>
      </ChHead>
      <ChBody>
        <ChGrid>
          <Controller
            control={control}
            name="machine"
            render={({ field: { value, onChange, onBlur } }) => (
              <Select
                label="Recording Machine Name"
                placeHolder="Select Recording Machine"
                size="middle"
                value={value || undefined}
                items={machineItems}
                onChange={onChange}
                onBlur={onBlur}
                errorMessage={errors.machine?.message}
                disabled={disabled}
                isRequired
                $fullWidth
              />
            )}
          />
          <Controller
            control={control}
            name="source"
            render={({ field: { value, onChange, onBlur } }) => (
              <Select
                label="Source Folder"
                placeHolder="Select Source Folder"
                size="middle"
                value={value || undefined}
                items={SOURCE_FOLDER_ITEMS}
                onChange={onChange}
                onBlur={onBlur}
                errorMessage={errors.source?.message}
                disabled={disabled}
                isRequired
                $fullWidth
              />
            )}
          />
          {/* Stored exactly as typed — capitalisation and spacing are preserved. */}
          <Controller
            control={control}
            name="folder"
            render={({ field: { value, onChange, onBlur } }) => (
              <Input
                type="text"
                label="Folder Name"
                placeholder={folderPlaceholder}
                size="middle"
                className="ps-mono"
                maxLength={32}
                spellCheck={false}
                autoComplete="off"
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                errorMessage={errors.folder?.message}
                disabled={disabled}
                isRequired
                $fullWidth
              />
            )}
          />
        </ChGrid>
        <ChFoot>
          <Controller
            control={control}
            name="ads"
            render={({ field: { value, onChange } }) => (
              <AdsBox>
                <Toggle
                  type="button"
                  role="switch"
                  aria-checked={value}
                  aria-label="Enable Ads Automation"
                  $on={value}
                  disabled={disabled}
                  onClick={() => onChange(!value)}
                />
                <div>
                  <ToggleLabel>Enable Ads Automation</ToggleLabel>
                  <ToggleSub>Advertisement monitoring for every program on this {label.toLowerCase()}</ToggleSub>
                </div>
              </AdsBox>
            )}
          />
          {!hasPrograms && !loading && (
            <Hint>Create a program first — {label.toLowerCase()} settings are saved through its programs.</Hint>
          )}
          <Button variant="outlined" onClick={onRevert} disabled={disabled || !isDirty} style={{ marginLeft: 'auto' }}>
            Revert
          </Button>
          <Button variant="primary" onClick={onSave} loading={saving} disabled={disabled}>
            Save {label} Settings
          </Button>
        </ChFoot>
      </ChBody>
    </ChCard>
  );
};
